'use client';

import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { SetupChoiceOptionRow } from '@/components/setup/experience/SetupChoiceOptionRow';
import { SETUP_PATH_MOTION } from '@/components/setup/experience/constants';
import { SetupStepFooter } from '@/components/setup/experience/SetupStepFooter';
import { SetupStepSection } from '@/components/setup/experience/SetupStepSection';
import {
  getSetupFunnelVerticalOptions,
  type SetupFunnelVertical,
} from '@/components/setup/experience/lib/setupFunnelGeneration';

interface SetupCheckoutVerticalStepProps {
  selected: SetupFunnelVertical | null;
  onSelect: (vertical: SetupFunnelVertical) => void;
  onContinue: () => void;
  onBack: () => void;
  /** Digital stores can't ship goods — physical verticals are hidden from the list. */
  isDigital: boolean;
}

/** Q1 of the guided funnel generation — "what do you sell?". Drives checkout theme + upsell. */
export function SetupCheckoutVerticalStep({
  selected,
  onSelect,
  onContinue,
  onBack,
  isDigital,
}: SetupCheckoutVerticalStepProps) {
  const { t } = useTranslation('setup');
  const options = getSetupFunnelVerticalOptions(isDigital);

  return (
    <SetupStepSection
      title={t('experience.checkout.vertical.title')}
      description={t('experience.checkout.vertical.description')}
      footer={
        <SetupStepFooter
          backLabel={t('footer.back')}
          onBack={onBack}
          primaryLabel={t('footer.continue')}
          onPrimary={onContinue}
          primaryDisabled={!selected}
        />
      }
    >
      <motion.ul
        className="m-0 flex list-none flex-col gap-3 p-0"
        role="listbox"
        aria-label={t('experience.checkout.vertical.title')}
        variants={SETUP_PATH_MOTION.list}
        initial="hidden"
        animate="show"
      >
        {options.map((option) => {
          const Icon = option.icon;
          return (
            <SetupChoiceOptionRow
              key={option.id}
              icon={
                <Icon
                  className="h-[1.375rem] w-[1.375rem] text-[color-mix(in_oklch,var(--foreground)_70%,var(--muted-foreground))]"
                  aria-hidden
                />
              }
              name={t(option.nameKey)}
              hint={t(option.hintKey)}
              selected={selected === option.id}
              onSelect={() => onSelect(option.id)}
            />
          );
        })}
      </motion.ul>
    </SetupStepSection>
  );
}
